import { useState, useRef } from 'react';
import { Type, Check } from 'lucide-react';
import { AiEditor } from '@/ai-editor';
import { Button } from '@/components/ui/button';

export function TextTile({ filter, onConfigChange }) {
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef(null);

  const content = filter?.content || '';

  const handleContentChange = (newContent) => {
    onConfigChange?.({ ...filter, content: newContent });
  };

  // No content set - show placeholder
  if (!content || content === '<p></p>') {
    return (
      <div ref={containerRef} className="h-full flex items-center justify-center p-2">
        {isEditing ? (
          <div className="w-full h-full flex flex-col relative">
            <AiEditor
              value={content}
              onChange={handleContentChange}
              placeholder="Write something..."
              className="flex-1 min-h-0 [&_.ProseMirror]:min-h-[100px]"
              showVoiceButton={false}
            />
            <Button
              size="sm"
              className="absolute bottom-2 right-2 z-20"
              onClick={() => setIsEditing(false)}
            >
              <Check className="h-4 w-4 mr-1" />
              Done
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <Type className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-500">Add Rich Text</span>
          </button>
        )}
      </div>
    );
  }

  // Content set - display it or edit
  return (
    <div ref={containerRef} className="relative h-full w-full group">
      {isEditing ? (
        <div className="w-full h-full flex flex-col relative">
          <AiEditor
            value={content}
            onChange={handleContentChange}
            placeholder="Write something..."
            className="flex-1 min-h-0 [&_.ProseMirror]:min-h-[100px]"
            showVoiceButton={false}
          />
          <Button
            size="sm"
            className="absolute bottom-2 right-2 z-20"
            onClick={() => setIsEditing(false)}
          >
            <Check className="h-4 w-4 mr-1" />
            Done
          </Button>
        </div>
      ) : (
        <>
          {/* Read-only content display */}
          <div
            className="prose prose-sm max-w-none h-full overflow-auto px-2 py-1 cursor-pointer [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: content }}
            onClick={() => setIsEditing(true)}
          />

          {/* Edit hint on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none rounded">
            <span className="bg-black/60 text-white px-2 py-1 rounded-full text-xs font-medium">
              Click to edit
            </span>
          </div>
        </>
      )}
    </div>
  );
}
